// react
import React, { useState, useEffect } from "react";

// prop-types
import PropTypes from "prop-types";

// context
import { UserAuth } from "context/AuthContext";

// react-i18next
import { useTranslation } from "react-i18next";

// @mui
import {
  useMediaQuery,
  useTheme,
  SwipeableDrawer,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  CardActions,
  Stack,
} from "@mui/material";
import {
  CloseOutlined as CloseOutlinedIcon,
  CheckCircle as CheckCircleIcon,
  Task as TaskIcon,
  Download as DownloadIcon,
  Verified as VerifiedIcon,
  AccessTime as AccessTimeIcon,
} from "@mui/icons-material";

// ui-components
import { Puller } from "ui-components/Puller";
import { LoadingDialog } from "ui-components/LoadingComponent";
import BlockchainVerificationDialog from "./BlockchainVerificationDialog";
import CDNFileUploadReceipt from "./SubmissionReportDialog";

// firebase
import { db } from "config/firebase";
import { doc, getDoc } from "firebase/firestore";

// storage
import downloadAsZip from "storage/downloadAsZip";

// verification
import { verifySignatureAIC } from "verification";

// A ---------------------------------------------------------------------- M

const softCard = (theme) => ({
  p: 2,
  borderRadius: 3,
  background: `linear-gradient(135deg, ${theme.palette.background.paper}F4, ${theme.palette.background.default}E8)`,
  border: `1px solid ${theme.palette.divider}40`,
  boxShadow: "0 4px 14px rgba(0,0,0,0.10)",
  backdropFilter: "blur(6px)",
  transition: "all 0.25s ease",
  "&:hover": {
    boxShadow: "0 6px 22px rgba(0,0,0,0.20)",
    transform: "translateY(-2px)",
  },
});

const SectionHeader = ({ icon, title }) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
    {icon}
    <Typography variant="subtitle1" fontWeight={700} color="text.secondary" sx={{ letterSpacing: "0.3px" }}>
      {title}
    </Typography>
  </Stack>
);

const ActionBar = ({ actions }) => (
  <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
    {actions.map(({ icon, title, onClick }, i) => (
      <Tooltip key={i} title={title}>
        <IconButton onClick={onClick}>{icon}</IconButton>
      </Tooltip>
    ))}
  </Stack>
);

const DocumentPreservationProofDialog = ({ record, open, onClose, handleDownloadFile }) => {
  const { user } = UserAuth();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [uploadReceipt, setUploadReceipt] = useState(null);
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [aicData, setAicData] = useState(null);
  const [verification, setVerification] = useState(null);
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!record) return;

    const fetchAll = async () => {
      try {
        if (record.txid) {
          const snap = await getDoc(doc(db, "uploadReceipts", record.txid));
          if (snap.exists()) setUploadReceipt(snap.data());
        }

        if (record.AIC_ID) {
          const snap = await getDoc(doc(db, "aicsdata", record.AIC_ID));
          if (snap.exists()) setAicData(snap.data());
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [record]);

  const handleOpenVerify = () => {
    setInspectorOpen(true);
    window.history.pushState(null, "");
  };

  const verify = async () => {
    try {
      setVerificationLoading(true);
      const result = await verifySignatureAIC(user.uid, record, record.type);
      setVerification(result);
      handleOpenVerify();
    } catch (err) {
      console.error("Error in verify:", err);
    } finally {
      setVerificationLoading(false);
    }
  };

  const PreservationContent = (
    <Grid container spacing={2} sx={{ p: 1 }}>
      {/* ----------------------- AIC ----------------------- */}
      {record.AIC_ID && aicData && (
        <Grid item xs={12}>
          <Card sx={(theme) => softCard(theme)}>
            <CardContent>
              <SectionHeader icon={<CheckCircleIcon color="success" />} title={t("aic_archival_information_collection")} />
              <Typography variant="body2" sx={{ opacity: 0.85, ml: 4 }}>
                {t("aic_generated_message")}
              </Typography>
            </CardContent>

            <CardActions sx={{ display: "flex", justifyContent: "flex-end", px: 1 }}>
              <ActionBar
                actions={[
                  {
                    title: t("download_aic"),
                    icon: <DownloadIcon color="primary" />,
                    onClick: () =>
                      handleDownloadFile({
                        storagePath: `AICFiles/${record.AIC_ID}/${record.AIC_ID}_AIC.xml`,
                        fileName: `${record.AIC_ID}_AIC.xml`,
                      }),
                  },
                  {
                    title: t("download_signed_aic"),
                    icon: <VerifiedIcon color="primary" />,
                    onClick: () =>
                      handleDownloadFile({
                        storagePath: `AICFiles/${record.AIC_ID}/${record.AIC_ID}_AIC_signed.xml.p7m`,
                        fileName: `${record.AIC_ID}_AIC_signed.xml.p7m`,
                      }),
                  },
                  {
                    title: t("download_timestamp"),
                    icon: <AccessTimeIcon color="primary" />,
                    onClick: () =>
                      handleDownloadFile({
                        storagePath: `AICFiles/${record.AIC_ID}/${record.AIC_ID}_timestamp.xml`,
                        fileName: `${record.AIC_ID}_timestamp.xml`,
                      }),
                  },
                  {
                    title: t("verify"),
                    icon: <CheckCircleIcon color="success" />,
                    onClick: verify,
                  },
                ]}
              />
            </CardActions>
          </Card>
        </Grid>
      )}

      {/* ----------------------- IDC ----------------------- */}
      <Grid item xs={12}>
        <Card sx={(theme) => softCard(theme)}>
          <CardContent>
            <SectionHeader icon={<CheckCircleIcon color="success" />} title={t("idc_preservation_index")} />
            <Typography variant="body2" sx={{ opacity: 0.85, ml: 4 }}>
              {t("idc_generated_message")}
            </Typography>
          </CardContent>

          <CardActions sx={{ display: "flex", justifyContent: "flex-end", px: 1 }}>
            <ActionBar
              actions={[
                {
                  title: t("download_idc"),
                  icon: <DownloadIcon color="primary" />,
                  onClick: () =>
                    handleDownloadFile({
                      storagePath: `${record.uploadID}/${record.id}_IdC.xml`,
                      fileName: `${record.id}_IdC.xml`,
                    }),
                },
                {
                  title: t("download_signed_idc"),
                  icon: <VerifiedIcon color="primary" />,
                  onClick: () =>
                    handleDownloadFile({
                      storagePath: `${record.uploadID}/${record.id}_IdC_signed.xml.p7m`,
                      fileName: `${record.id}_IdC_signed.xml.p7m`,
                    }),
                },
              ]}
            />
          </CardActions>
        </Card>
      </Grid>

      {/* ----------------------- FILE TECNICI ----------------------- */}
      <Grid item xs={12}>
        <Card sx={(theme) => softCard(theme)}>
          <CardContent>
            <SectionHeader icon={<CheckCircleIcon color="success" />} title={t("technical_files")} />

            <Stack spacing={1.8} sx={{ mt: 3 }}>
              {[
                { name: "gluer.xml", description: t("parameters_file") },
                { name: "metadata.xml", description: t("metadata_file") },
              ].map((file) => (
                <Box
                  key={file.name}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    p: 1,
                    borderRadius: 3,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <TaskIcon color="success" sx={{ mt: 0.4 }} />

                  <Box flexGrow={1} sx={{ ml: 2 }}>
                    <Typography fontWeight={700} sx={{ fontSize: "0.95rem" }}>
                      {file.name}
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      {file.description}
                    </Typography>

                    <Typography variant="caption" color="text.disabled">
                      MIME: application/xml
                    </Typography>
                  </Box>

                  <Tooltip title={t("download")}>
                    <IconButton
                      onClick={() =>
                        handleDownloadFile({
                          storagePath: `${record.uploadID}/${record.id}_${file.name}`,
                          fileName: `${record.id}_${file.name}`,
                        })
                      }
                    >
                      <DownloadIcon color="primary" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  if (loading) return <LoadingDialog open />;

  return (
    <>
      {isMobile ? (
        <SwipeableDrawer
          anchor="bottom"
          open={open}
          onClose={onClose}
          onOpen={() => {}}
          sx={{
            "& .MuiDrawer-paper": {
              width: "100%",
              height: "90%",
              borderTopLeftRadius: "4%",
              borderTopRightRadius: "4%",
            },
          }}
        >
          <Puller />

          <Box sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={700} textAlign="center" mb={3}>
              {t("preservation_proof")}
            </Typography>

            {PreservationContent}

            <Stack spacing={1.3} mt={3}>
              <Button fullWidth variant="contained" onClick={() => setReceiptDialogOpen(true)}>
                {t("open_submission_report")}
              </Button>

              <Button fullWidth variant="contained" onClick={() => downloadAsZip(record.uploadID, record.tdr)}>
                {t("download_as_zip")}
              </Button>
            </Stack>
          </Box>
        </SwipeableDrawer>
      ) : (
        <Dialog
          open={open}
          onClose={onClose}
          maxWidth="md"
          fullWidth
          sx={{
            "& .MuiPaper-root": {
              borderRadius: 4,
              background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.background.default}EE)`,
              backdropFilter: "blur(10px)",
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: "0 12px 32px rgba(0,0,0,0.4)",
              transition: "all 0.3s ease",
            },
          }}
          data-testid="preservation-proof-modal"
        >
          <DialogTitle>
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid item>
                <Typography variant="h6" fontWeight={700}>
                  {t("preservation_proof")}
                </Typography>
              </Grid>
              <Grid item>
                <Tooltip title={t("close")} placement="top">
                  <IconButton onClick={onClose} color="error" edge="end">
                    <CloseOutlinedIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </DialogTitle>

          <DialogContent>{PreservationContent}</DialogContent>

          <DialogActions sx={{ p: 2.5 }}>
            <Button
              variant="text"
              sx={{
                borderRadius: 3,
                textTransform: "none",
                fontWeight: 600,
                px: 2,
                py: 1,
              }}
              onClick={onClose}
            >
              {t("close")}
            </Button>

            <Button
              variant="contained"
              sx={{
                borderRadius: 3,
                textTransform: "none",
                fontWeight: 600,
                px: 2,
                py: 1,
              }}
              onClick={() => setReceiptDialogOpen(true)}
            >
              {t("submission_report")}
            </Button>

            <Button
              variant="contained"
              sx={{
                borderRadius: 3,
                textTransform: "none",
                fontWeight: 600,
                px: 2,
                py: 1,
              }}
              onClick={() => downloadAsZip(record.uploadID, record.tdr)}
            >
              {t("download_as_zip")}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {verificationLoading && <LoadingDialog open={verificationLoading} />}

      {receiptDialogOpen && uploadReceipt && <CDNFileUploadReceipt receiptDialogOpen={receiptDialogOpen} handleResetUploadReceipt={() => setReceiptDialogOpen(false)} uploadReceipt={uploadReceipt} />}

      <BlockchainVerificationDialog verification={verification} open={inspectorOpen} onClose={() => setInspectorOpen(false)} />
    </>
  );
};

DocumentPreservationProofDialog.propTypes = {
  record: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  handleDownloadFile: PropTypes.func.isRequired,
};

export default DocumentPreservationProofDialog;
